// import { apiClient } from "@/lib/apiClient" // Uncomment when using real API
import type { Item, CreateItemRequest } from "../types/items.types"

// Mock data for development/demo purposes
// Replace this with actual API calls in production
const MOCK_ITEMS: Item[] = [
  {
    id: "1",
    name: "Laptop",
    description: "High performance laptop",
    price: 1500,
    quantity: 10,
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "Mouse",
    description: "Wireless mouse",
    price: 25,
    quantity: 50,
    createdAt: "2024-01-16T10:00:00Z",
    updatedAt: "2024-01-16T10:00:00Z",
  },
  {
    id: "3",
    name: "Keyboard",
    description: "Mechanical keyboard",
    price: 120,
    quantity: 30,
    createdAt: "2024-01-17T10:00:00Z",
    updatedAt: "2024-01-17T10:00:00Z",
  },
]

// Simulate API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// In-memory storage for mock data
const itemsData = [...MOCK_ITEMS]

export const itemsApi = {
  /**
   * Get all items with pagination
   */
  getAll: async (params?: { page?: number; pageSize?: number; search?: string }) => {
    await delay(500) // Simulate API delay

    let filteredItems = [...itemsData]

    // Search filter
    if (params?.search) {
      const searchLower = params.search.toLowerCase()
      filteredItems = filteredItems.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower)
      )
    }

    // Pagination
    const page = params?.page || 1
    const pageSize = params?.pageSize || 10
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedItems = filteredItems.slice(startIndex, endIndex)

    return {
      data: paginatedItems,
      total: filteredItems.length,
      page,
      pageSize,
    }
  },

  /**
   * Get item by ID
   */
  getById: async (id: string) => {
    await delay(300)
    const item = itemsData.find((item) => item.id === id)
    if (!item) {
      throw new Error("Item not found")
    }
    return { data: item }
  },

  /**
   * Create new item
   */
  create: async (data: CreateItemRequest) => {
    await delay(500)
    const newItem: Item = {
      id: Date.now().toString(),
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    itemsData.push(newItem)
    return { data: newItem }
  },

  /**
   * Update item
   */
  update: async (id: string, data: Partial<CreateItemRequest>) => {
    await delay(500)
    const itemIndex = itemsData.findIndex((item) => item.id === id)
    if (itemIndex === -1) {
      throw new Error("Item not found")
    }
    itemsData[itemIndex] = {
      ...itemsData[itemIndex],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    return { data: itemsData[itemIndex] }
  },

  /**
   * Delete item
   */
  delete: async (id: string) => {
    await delay(300)
    const itemIndex = itemsData.findIndex((item) => item.id === id)
    if (itemIndex === -1) {
      throw new Error("Item not found")
    }
    itemsData.splice(itemIndex, 1)
    return { data: { id } }
  },
}

// For production, use actual API calls:
// export const itemsApi = {
//   getAll: (params?: Record<string, any>) =>
//     apiClient.get<Item[]>("/api/items", { params }),
//   getById: (id: string) => apiClient.get<Item>(`/api/items/${id}`),
//   create: (data: CreateItemRequest) => apiClient.post<Item>("/api/items", data),
//   update: (id: string, data: Partial<CreateItemRequest>) =>
//     apiClient.put<Item>(`/api/items/${id}`, data),
//   delete: (id: string) => apiClient.delete(`/api/items/${id}`),
// }

